/**
 * React Query mutation for updating user settings
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, ApiError } from '../lib/api';
import { useToast } from '../components/ui/Toast';
import { queryKeys } from '../lib/queryKeys';
import type { UserSettings } from './useSettingsQuery';

export interface UpdateSettingsInput {
  visibility?: 'public' | 'friends' | 'private';
  activity_visibility_enabled?: boolean;
  allow_recommendations?: 'followers' | 'none';
  show_in_search?: boolean;
}

/**
 * Update user's privacy settings with optimistic updates
 */
export function useUpdateSettings() {
  const { user, session, profile } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (updates: UpdateSettingsInput) => {
      if (!user || !token) {
        throw new Error('Not authenticated');
      }

      return apiFetch<UserSettings>('/user/settings', {
        method: 'PATCH',
        token,
        body: updates,
      });
    },
    onMutate: async (updates) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['settings', user?.id] });

      // Snapshot previous value
      const previousSettings = queryClient.getQueryData<UserSettings>(['settings', user?.id]);

      // Optimistically update
      if (previousSettings) {
        queryClient.setQueryData<UserSettings>(['settings', user?.id], {
          ...previousSettings,
          ...updates,
        });
      }

      return { previousSettings };
    },
    onError: (error, _updates, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData(['settings', user?.id], context.previousSettings);
      }

      const message = error instanceof ApiError ? error.message : 'Failed to update settings';
      toast.error(message);
    },
    onSuccess: (result) => {
      toast.success('Settings updated');

      // Write canonical result to settings cache (ensures optimistic update is replaced with server truth)
      queryClient.setQueryData<UserSettings>(['settings', user?.id], result);

      // Cross-write visibility to profile cache if it exists (instant sync, no refetch needed)
      if (profile?.username_lower) {
        const profileKey = queryKeys.profile.byUsername(profile.username_lower);
        // Only update if cache entry exists (don't synthesize partial profile)
        queryClient.setQueryData(profileKey, (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const prevProfile = prev as Record<string, unknown>;
          return {
            ...prevProfile,
            visibility: result.visibility,
            // Keep is_public in sync if present
            ...(('is_public' in prevProfile) ? { is_public: result.visibility === 'public' } : {}),
          };
        });
      }
    },
    onSettled: () => {
      // Backstop invalidation (will refetch on window focus if stale)
      queryClient.invalidateQueries({ queryKey: ['settings', user?.id] });
      if (profile?.username_lower) {
        queryClient.invalidateQueries({ queryKey: queryKeys.profile.byUsername(profile.username_lower) });
      }
    },
  });
}
